import React, {
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import classNames from '../../../utils/classNames';
import { getMonthGrid, getCellKey } from '../../../utils/eventsCalendar';
import ChevronIcon from '../../atoms/icons/ChevronIcon';
import AnchoredPortal from '../AnchoredPortal';
import './index.scss';

/* ===========================================================================
 * GlassDatePicker
 * --------------------------------------------------------------------------
 * Reusable dark-glass date / datetime picker. Replaces native
 * <input type="date|datetime-local"> with a custom popover that matches the
 * rest of the calendar redesign aesthetic.
 *
 * Value contract — mirrors the native input format:
 *   withTime=false  → "YYYY-MM-DD"
 *   withTime=true   → "YYYY-MM-DDTHH:mm"  (local, no timezone suffix)
 *
 * onChange receives an event-shaped object so consumers expecting
 * `event.target.value` keep working.
 *
 * `portal` lifts the popover to the app's portal root — needed when the trigger
 * sits in a clipping or scrolling box.
 * ========================================================================= */

function pad(n) {
    return String(n).padStart(2, '0');
}

function parseValue(value) {
    if (!value) return null;
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }
    // Accept the same shapes the native inputs produce: "YYYY-MM-DD" or "YYYY-MM-DDTHH:mm".
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
        const [datePart, timePart] = value.split('T');
        const [y, m, d] = datePart.split('-').map((s) => parseInt(s, 10));
        let hours = 0;
        let minutes = 0;
        if (timePart) {
            const [hh, mm] = timePart.split(':').map((s) => parseInt(s, 10));
            if (Number.isFinite(hh)) hours = hh;
            if (Number.isFinite(mm)) minutes = mm;
        }
        if ([y, m, d].every(Number.isFinite)) {
            return new Date(y, m - 1, d, hours, minutes, 0, 0);
        }
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function startOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
}

function startOfDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
}

function daysInMonth(date) {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}

/* Same day-of-month in another month, pulled back to its last day when short. */
function sameDayInMonth(day, month) {
    return new Date(month.getFullYear(), month.getMonth(), Math.min(day, daysInMonth(month)));
}

function addDays(date, count) {
    const next = new Date(date);
    next.setDate(next.getDate() + count);
    return startOfDay(next);
}

const GRID_STEP = {
    ArrowLeft: -1,
    ArrowRight: 1,
    ArrowUp: -7,
    ArrowDown: 7,
};

function sameYMD(a, b) {
    return a && b
        && a.getFullYear() === b.getFullYear()
        && a.getMonth() === b.getMonth()
        && a.getDate() === b.getDate();
}

function formatLocalDate(date) {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function formatLocalDateTime(date) {
    return `${formatLocalDate(date)}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function formatDatePart(date, locale) {
    if (!date) return '';
    try {
        return new Intl.DateTimeFormat(locale, {
            year: 'numeric', month: 'short', day: 'numeric',
        }).format(date);
    } catch (error) {
        return formatLocalDate(date);
    }
}

function formatTimePart(date, locale) {
    if (!date) return '';
    try {
        return new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(date);
    } catch (error) {
        return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }
}

const CalendarIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
);

export default function GlassDatePicker({
    id,
    name,
    label,
    value,
    onChange,
    withTime = false,
    disabled = false,
    placeholder,
    required = false,
    min,
    max,
    ariaLabel,
    className = '',
    weekStartsOn = 1,
    portal = false,
}) {
    const { t, i18n } = useTranslation();
    const locale = i18n.resolvedLanguage || i18n.language || 'en-US';
    const fallbackId = useId();
    const baseId = id || fallbackId;
    const labelId = `${baseId}-label`;
    const popoverId = `${baseId}-popover`;

    const containerRef = useRef(null);
    const triggerRef = useRef(null);
    const popoverRef = useRef(null);
    const activeCellRef = useRef(null);
    /* Focus follows the grid's active cell only on open and on arrow keys —
       never on a re-render, which would yank it back out of the time field. */
    const pullFocusRef = useRef(false);

    const parsedValue = useMemo(() => parseValue(value), [value]);

    const [isOpen, setIsOpen] = useState(false);
    const [monthDate, setMonthDate] = useState(() => startOfMonth(parsedValue || new Date()));
    /* The grid is a roving tabindex: one day is tabbable, the arrows move it. */
    const [activeDate, setActiveDate] = useState(() => startOfDay(parsedValue || new Date()));

    // Re-anchor the visible month whenever the consumer pushes a new value
    // (lets a programmatic update bring the picker to the right month).
    useEffect(() => {
        if (parsedValue) setMonthDate(startOfMonth(parsedValue));
    }, [parsedValue]);

    // Outside click + Escape close the popover. In portal mode the popover is
    // not inside `containerRef`, so AnchoredPortal owns both gestures instead.
    useEffect(() => {
        if (!isOpen || portal) return undefined;
        const handlePointerDown = (event) => {
            if (!containerRef.current?.contains(event.target)) setIsOpen(false);
        };
        const handleKey = (event) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
                triggerRef.current?.focus();
            }
        };
        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('touchstart', handlePointerDown);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
            document.removeEventListener('keydown', handleKey);
        };
    }, [isOpen, portal]);

    const minDate = useMemo(() => parseValue(min), [min]);
    const maxDate = useMemo(() => parseValue(max), [max]);

    const today = useMemo(() => new Date(), []);
    const days = useMemo(
        () => getMonthGrid(monthDate, weekStartsOn, today),
        [monthDate, weekStartsOn, today],
    );

    const monthHeading = useMemo(() => {
        try {
            return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(monthDate);
        } catch (error) {
            return '';
        }
    }, [locale, monthDate]);

    // Localized weekday labels in the configured week start order.
    const weekdayLabels = useMemo(() => {
        // Reference week — Mon Jan 1 2024 .. Sun Jan 7 2024
        const ref = new Date(2024, 0, 1);
        const refDow = ref.getDay(); // 1 (Mon)
        const offset = (weekStartsOn - refDow + 7) % 7;
        return Array.from({ length: 7 }, (_, i) => {
            const day = new Date(ref);
            day.setDate(ref.getDate() + offset + i);
            try {
                return new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(day);
            } catch (error) {
                return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][day.getDay()];
            }
        });
    }, [locale, weekStartsOn]);

    const openPicker = useCallback(() => {
        const base = startOfDay(parsedValue || new Date());
        setActiveDate(base);
        setMonthDate(startOfMonth(base));
        pullFocusRef.current = true;
        setIsOpen(true);
    }, [parsedValue]);

    const closePicker = useCallback(() => {
        setIsOpen(false);
        triggerRef.current?.focus();
    }, []);

    const goPrev = useCallback(() => {
        setMonthDate((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1));
    }, []);
    const goNext = useCallback(() => {
        setMonthDate((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1));
    }, []);
    const goToday = useCallback(() => {
        const today = startOfDay(new Date());
        setMonthDate(startOfMonth(today));
        setActiveDate(today);
    }, []);

    const isOutOfRange = useCallback((date) => {
        if (minDate && startOfDay(date) < startOfDay(minDate)) return true;
        if (maxDate && startOfDay(date) > startOfDay(maxDate)) return true;
        return false;
    }, [minDate, maxDate]);

    const moveActive = useCallback((nextDate) => {
        // A day outside min/max renders disabled, so it cannot hold focus.
        if (isOutOfRange(nextDate)) return;
        setActiveDate(nextDate);
        setMonthDate((m) => (m.getMonth() === nextDate.getMonth() && m.getFullYear() === nextDate.getFullYear()
            ? m
            : startOfMonth(nextDate)));
        pullFocusRef.current = true;
    }, [isOutOfRange]);

    // Stepping the month with the nav buttons keeps focus on the button; the
    // grid's tab stop is carried into the month on view.
    useEffect(() => {
        if (!isOpen) return;
        setActiveDate((current) => (current
            && current.getMonth() === monthDate.getMonth()
            && current.getFullYear() === monthDate.getFullYear()
            ? current
            : sameDayInMonth(current ? current.getDate() : 1, monthDate)));
    }, [isOpen, monthDate]);

    const emit = useCallback((nextDate) => {
        if (typeof onChange !== 'function') return;
        const next = nextDate
            ? (withTime ? formatLocalDateTime(nextDate) : formatLocalDate(nextDate))
            : '';
        onChange({ target: { value: next, name } });
    }, [name, onChange, withTime]);

    const handleSelectDay = useCallback((date) => {
        if (isOutOfRange(date)) return;
        let next;
        if (withTime) {
            const baseTime = parsedValue || new Date();
            next = new Date(date);
            next.setHours(baseTime.getHours(), baseTime.getMinutes(), 0, 0);
        } else {
            next = startOfDay(date);
        }
        emit(next);
        if (!withTime) {
            setIsOpen(false);
            triggerRef.current?.focus();
        }
    }, [emit, isOutOfRange, parsedValue, withTime]);

    const handleTimeBlur = useCallback((event) => {
        const raw = event.target.value;
        if (!raw) return;
        const [hh, mm] = raw.split(':').map((s) => parseInt(s, 10));
        if (!Number.isFinite(hh) || !Number.isFinite(mm)) return;
        const base = parsedValue || new Date();
        const next = new Date(base);
        next.setHours(hh, mm, 0, 0);
        emit(next);
    }, [emit, parsedValue]);

    const handleClear = useCallback(() => {
        emit(null);
        closePicker();
    }, [closePicker, emit]);

    const handleDone = closePicker;

    const handleTriggerClick = useCallback(() => {
        if (disabled) return;
        if (isOpen) closePicker();
        else openPicker();
    }, [closePicker, disabled, isOpen, openPicker]);

    const handleTriggerKeyDown = useCallback((event) => {
        if (disabled) return;
        if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openPicker();
        }
    }, [disabled, openPicker]);

    const handleGridKeyDown = useCallback((event) => {
        const step = GRID_STEP[event.key];
        if (step) {
            event.preventDefault();
            moveActive(addDays(activeDate, step));
            return;
        }
        if (event.key === 'Home' || event.key === 'End') {
            event.preventDefault();
            const offset = (activeDate.getDay() - weekStartsOn + 7) % 7;
            moveActive(addDays(activeDate, event.key === 'Home' ? -offset : 6 - offset));
            return;
        }
        if (event.key === 'PageUp' || event.key === 'PageDown') {
            event.preventDefault();
            const delta = event.key === 'PageUp' ? -1 : 1;
            const target = new Date(activeDate.getFullYear(), activeDate.getMonth() + delta, 1);
            moveActive(sameDayInMonth(activeDate.getDate(), target));
        }
    }, [activeDate, moveActive, weekStartsOn]);

    /* The popover is portaled to the end of the document, so Tab would otherwise
       walk the rest of the page before reaching the calendar. */
    const handlePopoverKeyDown = useCallback((event) => {
        if (event.key !== 'Tab') return;
        const stops = Array.from(
            popoverRef.current?.querySelectorAll('button, input, [tabindex]') || [],
        ).filter((node) => !node.disabled && node.tabIndex >= 0);
        if (stops.length === 0) return;
        const first = stops[0];
        const last = stops[stops.length - 1];
        const { activeElement } = document;
        if (event.shiftKey && activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    }, []);

    useEffect(() => {
        if (!isOpen || !pullFocusRef.current) return;
        pullFocusRef.current = false;
        activeCellRef.current?.focus({ preventScroll: true });
    }, [isOpen, activeDate, monthDate]);

    const dateText = formatDatePart(parsedValue, locale);
    const timeText = withTime ? formatTimePart(parsedValue, locale) : '';
    const placeholderText = placeholder
        || (withTime
            ? t('common.datePicker.placeholder.datetime', 'Pick a date and time')
            : t('common.datePicker.placeholder.date', 'Pick a date'));
    const timeValue = parsedValue ? `${pad(parsedValue.getHours())}:${pad(parsedValue.getMinutes())}` : '';
    const triggerLabelledBy = label ? labelId : undefined;

    const popoverHost = (content) => (portal ? (
        <AnchoredPortal
            anchorRef={triggerRef}
            onClose={handleDone}
            placement="bottom-start"
            offset={{ x: 0, y: 8 }}
        >
            {content}
        </AnchoredPortal>
    ) : content);

    return (
        <div
            ref={containerRef}
            className={classNames(
                'glass-date-picker',
                withTime && 'glass-date-picker--with-time',
                disabled && 'glass-date-picker--disabled',
                isOpen && 'glass-date-picker--open',
                className,
            )}
        >
            {label ? (
                <label className="glass-date-picker__label-hidden" id={labelId} htmlFor={baseId}>
                    {label}
                </label>
            ) : null}
            <button
                ref={triggerRef}
                id={baseId}
                name={name}
                type="button"
                className="glass-date-picker__trigger"
                aria-haspopup="dialog"
                aria-expanded={isOpen}
                aria-controls={popoverId}
                aria-labelledby={triggerLabelledBy}
                aria-label={ariaLabel}
                aria-disabled={disabled || undefined}
                disabled={disabled}
                onClick={handleTriggerClick}
                onKeyDown={handleTriggerKeyDown}
            >
                <span className="glass-date-picker__icon" aria-hidden="true">
                    <CalendarIcon />
                </span>
                <span className="glass-date-picker__value">
                    {parsedValue ? (
                        <>
                            <span className="glass-date-picker__date">{dateText}</span>
                            {timeText ? (
                                <span className="glass-date-picker__time-value">{timeText}</span>
                            ) : null}
                        </>
                    ) : (
                        <span className="glass-date-picker__placeholder">{placeholderText}</span>
                    )}
                </span>
                <span className="glass-date-picker__caret" aria-hidden="true">▾</span>
            </button>

            {isOpen ? popoverHost(
                <div
                    ref={popoverRef}
                    id={popoverId}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby={triggerLabelledBy}
                    /* Consumers that name the trigger with `ariaLabel` rather
                       than `label` would otherwise leave the dialog unnamed. */
                    aria-label={triggerLabelledBy ? undefined : ariaLabel}
                    onKeyDown={handlePopoverKeyDown}
                    className={classNames(
                        'glass-date-picker__popover',
                        portal && 'glass-date-picker__popover--portal',
                    )}
                >
                    <header className="glass-date-picker__head">
                        <button
                            type="button"
                            className="glass-date-picker__nav-btn"
                            onClick={goPrev}
                            aria-label={t('common.datePicker.prevMonth', 'Previous month')}
                        >
                            <ChevronIcon direction="left" />
                        </button>
                        <button
                            type="button"
                            className="glass-date-picker__month-btn"
                            onClick={goToday}
                            aria-label={t('common.datePicker.goToToday', 'Go to today')}
                        >
                            {monthHeading}
                        </button>
                        <button
                            type="button"
                            className="glass-date-picker__nav-btn"
                            onClick={goNext}
                            aria-label={t('common.datePicker.nextMonth', 'Next month')}
                        >
                            <ChevronIcon direction="right" />
                        </button>
                    </header>

                    <div className="glass-date-picker__weekdays" aria-hidden="true">
                        {weekdayLabels.map((wk, idx) => (
                            <span key={idx}>{wk}</span>
                        ))}
                    </div>

                    <div
                        className="glass-date-picker__days"
                        role="grid"
                        aria-label={monthHeading}
                        onKeyDown={handleGridKeyDown}
                    >
                        {days.map((row, rowIdx) => (
                            row.map((cell) => {
                                const isSelected = parsedValue && sameYMD(parsedValue, cell.date);
                                const outOfRange = isOutOfRange(cell.date);
                                const isActive = sameYMD(activeDate, cell.date);
                                return (
                                    <button
                                        key={`${rowIdx}-${getCellKey(cell.date)}`}
                                        ref={isActive ? activeCellRef : undefined}
                                        type="button"
                                        role="gridcell"
                                        aria-selected={Boolean(isSelected)}
                                        aria-current={cell.isToday ? 'date' : undefined}
                                        tabIndex={isActive ? 0 : -1}
                                        disabled={outOfRange}
                                        className={classNames(
                                            'glass-date-picker__day',
                                            !cell.isCurrentMonth && 'glass-date-picker__day--muted',
                                            cell.isToday && 'glass-date-picker__day--today',
                                            isSelected && 'glass-date-picker__day--selected',
                                        )}
                                        onClick={() => {
                                            setActiveDate(startOfDay(cell.date));
                                            handleSelectDay(cell.date);
                                        }}
                                    >
                                        {cell.date.getDate()}
                                    </button>
                                );
                            })
                        ))}
                    </div>

                    {withTime ? (
                        <div className="glass-date-picker__time">
                            <span className="glass-date-picker__time-label">
                                {t('common.datePicker.timeLabel', 'Time')}
                            </span>
                            <input
                                type="time"
                                className="glass-date-picker__time-input"
                                aria-label={t('common.datePicker.timeLabel', 'Time')}
                                value={timeValue}
                                onChange={handleTimeBlur}
                                step="60"
                            />
                        </div>
                    ) : null}

                    <footer className="glass-date-picker__foot">
                        <button
                            type="button"
                            className="glass-date-picker__action glass-date-picker__action--ghost"
                            onClick={handleClear}
                            disabled={!parsedValue}
                        >
                            {t('common.datePicker.clear', 'Clear')}
                        </button>
                        <button
                            type="button"
                            className="glass-date-picker__action glass-date-picker__action--primary"
                            onClick={handleDone}
                        >
                            {t('common.datePicker.done', 'Done')}
                        </button>
                    </footer>
                </div>
            ) : null}
        </div>
    );
}

GlassDatePicker.propTypes = {
    id: PropTypes.string,
    name: PropTypes.string,
    label: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func,
    withTime: PropTypes.bool,
    disabled: PropTypes.bool,
    placeholder: PropTypes.string,
    required: PropTypes.bool,
    min: PropTypes.string,
    max: PropTypes.string,
    ariaLabel: PropTypes.string,
    className: PropTypes.string,
    portal: PropTypes.bool,
    weekStartsOn: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6]),
};
